import { HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocMap } from '@cm/core/localization';

const pathStart = 'Candidates|System Activity|Profile Audit|';
const sharedProperties: Pick<LocDef, 'shared' | 'htmlAllowed' | 'portal'> = {
	shared: false,
	htmlAllowed: false,
	portal: 'SP',
};

type LocNameStart = 'SPProfileAudit';
export type SPProfileAuditLocNames =
	| `${LocNameStart}AffiliateId`
	| `${LocNameStart}AffiliationEmail`
	| `${LocNameStart}AffiliationName`
	| `${LocNameStart}TitleTab`
	| `${LocNameStart}TitlePage`
	| `${LocNameStart}ContentUpdatorCandidate`
	| `${LocNameStart}LabelFilterByType`
	| `${LocNameStart}LabelFilterByField`
	| `${LocNameStart}LabelDate`
	| `${LocNameStart}LabelType`
	| `${LocNameStart}LabelField`
	| `${LocNameStart}LabelCurrentValue`
	| `${LocNameStart}LabelPreviousValue`
	| `${LocNameStart}LabelUpdator`
	| `${LocNameStart}ContentSystemActivityType`
	| `${LocNameStart}ContentPrimaryInfoType`
	| `${LocNameStart}ContentLocalizedPrimaryInfoType`
	| `${LocNameStart}ContentAddressType`
	| `${LocNameStart}ContentOtherAddressType`
	| `${LocNameStart}ContentLocalizedAddressType`
	| `${LocNameStart}ContentAffiliationType`
	| `${LocNameStart}ContentRowId`
	| `${LocNameStart}ContentCmcId`
	| `${LocNameStart}ContentEffective`
	| `${LocNameStart}ContentUpdator`
	| `${LocNameStart}ContentUpdated`
	| `${LocNameStart}ContentImportDpId`
	| `${LocNameStart}ContentImportSource`
	| `${LocNameStart}ContentNameFirst`
	| `${LocNameStart}ContentNameLast`
	| `${LocNameStart}ContentNameInvalid`
	| `${LocNameStart}ContentPreferredNameFirst`
	| `${LocNameStart}ContentPreferredNameLast`
	| `${LocNameStart}ContentEmail`
	| `${LocNameStart}ContentHasPassword`
	| `${LocNameStart}ContentCompanyName`
	| `${LocNameStart}ContentAddr1Line1`
	| `${LocNameStart}ContentAddr1Line2`
	| `${LocNameStart}ContentAddr1Line3`
	| `${LocNameStart}ContentAddr1City`
	| `${LocNameStart}ContentAddr1State`
	| `${LocNameStart}ContentAddr1PostalCode`
	| `${LocNameStart}ContentAddr1CountryCode`
	| `${LocNameStart}ContentAddr1PhoneNumber`
	| `${LocNameStart}ContentAddr1FaxNumber`
	| `${LocNameStart}ContentAddr1Business`
	| `${LocNameStart}ContentAddr2Line1`
	| `${LocNameStart}ContentAddr2Line2`
	| `${LocNameStart}ContentAddr2Line3`
	| `${LocNameStart}ContentAddr2City`
	| `${LocNameStart}ContentAddr2State`
	| `${LocNameStart}ContentAddr2PostalCode`
	| `${LocNameStart}ContentAddr2CountryCode`
	| `${LocNameStart}ContentAddr2PhoneNumber`
	| `${LocNameStart}ContentAddr2FaxNumber`
	| `${LocNameStart}ContentAddr2Business`
	| `${LocNameStart}ContentLocNameFirst`
	| `${LocNameStart}ContentLocNameLast`
	| `${LocNameStart}ContentLocCompanyName`
	| `${LocNameStart}ContentLocAddr1Line1`
	| `${LocNameStart}ContentLocAddr1Line2`
	| `${LocNameStart}ContentLocAddr1Line3`
	| `${LocNameStart}ContentLocAddr1City`
	| `${LocNameStart}ContentLocAddr1State`
	| `${LocNameStart}ContentLocAddr1PostalCode`
	| `${LocNameStart}ContentAffiliation`;

export const spProfileAuditLocs: LocDefs<SPProfileAuditLocNames> = {
	SPProfileAuditAffiliateId: {
		defaultValue: 'Affiliate ID',
		description: 'Label for the Affiliate ID field in the Profile Audit section',
		displayPath: `${pathStart}Affiliate ID`,
		displayType: 'Label',
		feature: HubFeature.CandidateProfilePrimaryInformationAffiliation,
		...sharedProperties,
	},
	SPProfileAuditAffiliationEmail: {
		defaultValue: 'Affiliation Email',
		description: 'Label for the Affiliation Email field in the Profile Audit section',
		displayPath: `${pathStart}Affiliation Email`,
		displayType: 'Label',
		feature: HubFeature.CandidateProfilePrimaryInformationAffiliation,
		...sharedProperties,
	},
	SPProfileAuditAffiliationName: {
		defaultValue: 'Affiliation Name',
		description: 'Label for the Affiliation Name field in the Profile Audit section',
		displayPath: `${pathStart}Affiliation Name`,
		displayType: 'Label',
		feature: HubFeature.CandidateProfilePrimaryInformationAffiliation,
		...sharedProperties,
	},
	SPProfileAuditTitleTab: {
		defaultValue: 'Profile Audit',
		description: 'Tab Title',
		displayPath: `${pathStart}Tab Title`,
		displayType: 'Title',
		legacyName: 'CandidateProfile/Candidates.ProfileAudit.Title.Tab',
		feature: HubFeature.CandidateProfile,
		...sharedProperties,
	},
	SPProfileAuditTitlePage: {
		defaultValue: 'Profile Audit',
		description: 'Page Title',
		displayPath: `${pathStart}Page Title`,
		displayType: 'Title',
		legacyName: 'CandidateProfile/Candidates.ProfileAudit.Title.PageSection',
		feature: HubFeature.CandidateProfile,
		...sharedProperties,
	},
	SPProfileAuditLabelFilterByType: {
		defaultValue: 'Filter by Type',
		description: 'Label for : Filter by Type.',
		displayPath: `${pathStart}Filter by Type`,
		displayType: 'Label',
		legacyName: 'CandidateProfile/Candidates.ProfileAudit.FieldLabel.FilterByType',
		feature: HubFeature.CandidateProfile,
		...sharedProperties,
	},
	SPProfileAuditLabelFilterByField: {
		defaultValue: 'Filter by Field',
		description: 'Label for : Filter by Field.',
		displayPath: `${pathStart}Filter by Field`,
		displayType: 'Label',
		legacyName: 'CandidateProfile/Candidates.ProfileAudit.FieldLabel.FilterByField',
		feature: HubFeature.CandidateProfile,
		...sharedProperties,
	},
	SPProfileAuditContentUpdatorCandidate: {
		defaultValue: 'Candidate',
		description: 'Text shown when the updator is a candidate',
		displayPath: `${pathStart}Candidate Updator`,
		displayType: 'Content',
		legacyName: 'CandidateProfile/Candidates.ProfileAudit.Content.Updator',
		feature: HubFeature.CandidateProfile,
		...sharedProperties,
	},
	SPProfileAuditLabelDate: {
		defaultValue: 'Date',
		description: 'Column heading for: Date.',
		displayPath: `${pathStart}Date`,
		displayType: 'Label',
		legacyName: 'CandidateProfile/Candidates.ProfileAudit.ColumnHeading.Date',
		feature: HubFeature.CandidateProfile,
		...sharedProperties,
	},
	SPProfileAuditLabelType: {
		defaultValue: 'Type',
		description: 'Column heading for: Type.',
		displayPath: `${pathStart}Type`,
		displayType: 'Label',
		legacyName: 'CandidateProfile/Candidates.ProfileAudit.ColumnHeading.Type',
		feature: HubFeature.CandidateProfile,
		...sharedProperties,
	},
	SPProfileAuditLabelField: {
		defaultValue: 'Field',
		description: 'Column heading for: Field.',
		displayPath: `${pathStart}Field`,
		displayType: 'Label',
		legacyName: 'CandidateProfile/Candidates.ProfileAudit.ColumnHeading.Field',
		feature: HubFeature.CandidateProfile,
		...sharedProperties,
	},
	SPProfileAuditLabelCurrentValue: {
		defaultValue: 'Current Value',
		description: 'Column heading for: Current Value.',
		displayPath: `${pathStart}Current Value`,
		displayType: 'Label',
		legacyName: 'CandidateProfile/Candidates.ProfileAudit.ColumnHeading.CurrentValue',
		feature: HubFeature.CandidateProfile,
		...sharedProperties,
	},
	SPProfileAuditLabelPreviousValue: {
		defaultValue: 'Previous Value',
		description: 'Column heading for: Previous Value.',
		displayPath: `${pathStart}Previous Value`,
		displayType: 'Label',
		legacyName: 'CandidateProfile/Candidates.ProfileAudit.ColumnHeading.PreviousValue',
		feature: HubFeature.CandidateProfile,
		...sharedProperties,
	},
	SPProfileAuditLabelUpdator: {
		defaultValue: 'Updator',
		description: 'Column heading for: Updator.',
		displayPath: `${pathStart}Updator`,
		displayType: 'Label',
		legacyName: 'CandidateProfile/Candidates.ProfileAudit.ColumnHeading.Updator',
		feature: HubFeature.CandidateProfile,
		...sharedProperties,
	},
	SPProfileAuditContentSystemActivityType: {
		defaultValue: 'System Activity',
		description: 'Text shown for System Activity.',
		displayPath: `${pathStart}System Activity`,
		displayType: 'Content',
		legacyName: 'CandidateProfile/Candidates.ProfileAudit.Content.SystemActivityType',
		feature: HubFeature.CandidateProfile,
		...sharedProperties,
	},
	SPProfileAuditContentPrimaryInfoType: {
		defaultValue: 'Profile Information',
		description: 'Text shown for Profile Information.',
		displayPath: `${pathStart}Profile Information`,
		displayType: 'Content',
		legacyName: 'CandidateProfile/Candidates.ProfileAudit.Content.PrimaryInfoType',
		feature: HubFeature.CandidateProfile,
		...sharedProperties,
	},
	SPProfileAuditContentLocalizedPrimaryInfoType: {
		defaultValue: 'Non-Romanized Profile Information',
		description: 'Text shown for Non-Romanized Profile Information.',
		displayPath: `${pathStart}Non-Romanized Profile Information`,
		displayType: 'Content',
		legacyName: 'CandidateProfile/Candidates.ProfileAudit.Content.LocalizedPrimaryInfoType',
		feature: HubFeature.CandidateProfile,
		...sharedProperties,
	},
	SPProfileAuditContentAddressType: {
		defaultValue: 'Address',
		description: 'Text shown for Address.',
		displayPath: `${pathStart}Address`,
		displayType: 'Content',
		legacyName: 'CandidateProfile/Candidates.ProfileAudit.Content.AddressType',
		feature: HubFeature.CandidateProfile,
		...sharedProperties,
	},
	SPProfileAuditContentOtherAddressType: {
		defaultValue: 'Other Address',
		description: 'Text shown for Other Address.',
		displayPath: `${pathStart}Other Address`,
		displayType: 'Content',
		legacyName: 'CandidateProfile/Candidates.ProfileAudit.Content.OtherAddressType',
		feature: HubFeature.CandidateProfile,
		...sharedProperties,
	},
	SPProfileAuditContentLocalizedAddressType: {
		defaultValue: 'Non-Romanized Address',
		description: 'Text shown for Non-Romanized Address.',
		displayPath: `${pathStart}Non-Romanized Address`,
		displayType: 'Content',
		legacyName: 'CandidateProfile/Candidates.ProfileAudit.Content.LocalizedAddressType',
		feature: HubFeature.CandidateProfile,
		...sharedProperties,
	},
	SPProfileAuditContentAffiliationType: {
		defaultValue: 'Affiliation',
		description: 'Text shown for Affiliation.',
		displayPath: `${pathStart}Affiliation`,
		displayType: 'Content',
		legacyName: 'CandidateProfile/Candidates.ProfileAudit.Content.AffiliationType',
		feature: HubFeature.CandidateProfile,
		...sharedProperties,
	},
	SPProfileAuditContentRowId: {
		defaultValue: 'Row ID',
		description: 'Text shown for Row ID.',
		displayPath: `${pathStart}Row ID`,
		displayType: 'Content',
		legacyName: 'CandidateProfile/Candidates.ProfileAudit.Content.RowID',
		feature: HubFeature.CandidateProfile,
		...sharedProperties,
	},
	SPProfileAuditContentCmcId: {
		defaultValue: 'Cmc ID',
		description: 'Text shown for Cmc ID.',
		displayPath: `${pathStart}Cmc ID`,
		displayType: 'Content',
		legacyName: 'CandidateProfile/Candidates.ProfileAudit.Content.CmcID',
		feature: HubFeature.CandidateProfile,
		...sharedProperties,
	},
	SPProfileAuditContentEffective: {
		defaultValue: 'Effective Date',
		description: 'Text shown for Effective Date.',
		displayPath: `${pathStart}Effective Date`,
		displayType: 'Content',
		legacyName:
			'CandidateProfile.PrimaryInformation/Candidates.ProfileAudit.Content.EffectiveDate',
		feature: HubFeature.CandidateProfilePrimaryInformation,
		...sharedProperties,
	},
	SPProfileAuditContentUpdator: {
		defaultValue: 'Updator',
		description: 'Text shown for Updator.',
		displayPath: `${pathStart}Updator`,
		displayType: 'Content',
		legacyName: 'CandidateProfile.PrimaryInformation/Candidates.ProfileAudit.Content.Updator',
		feature: HubFeature.CandidateProfilePrimaryInformation,
		...sharedProperties,
	},
	SPProfileAuditContentUpdated: {
		defaultValue: 'Updated Date',
		description: 'Text shown for Updated Date.',
		displayPath: `${pathStart}Updated Date`,
		displayType: 'Content',
		legacyName:
			'CandidateProfile.PrimaryInformation/Candidates.ProfileAudit.Content.UpdatedDate',
		feature: HubFeature.CandidateProfilePrimaryInformation,
		...sharedProperties,
	},
	SPProfileAuditContentImportDpId: {
		defaultValue: 'Import Data Provider ID',
		description: 'Text shown for Import Data Provider ID.',
		displayPath: `${pathStart}Import Data Provider ID`,
		displayType: 'Content',
		legacyName:
			'CandidateProfile.PrimaryInformation/Candidates.ProfileAudit.Content.ImportDataProviderID',
		feature: HubFeature.CandidateProfilePrimaryInformation,
		...sharedProperties,
	},
	SPProfileAuditContentImportSource: {
		defaultValue: 'Import Source',
		description: 'Text shown for Import Source.',
		displayPath: `${pathStart}Import Source`,
		displayType: 'Content',
		legacyName:
			'CandidateProfile.PrimaryInformation/Candidates.ProfileAudit.Content.ImportSource',
		feature: HubFeature.CandidateProfilePrimaryInformation,
		...sharedProperties,
	},
	SPProfileAuditContentNameFirst: {
		defaultValue: 'First Name',
		description: 'Text shown for First Name.',
		displayPath: `${pathStart}First Name`,
		displayType: 'Content',
		legacyName: 'CandidateProfile.PrimaryInformation/Candidates.ProfileAudit.Content.FirstName',
		feature: HubFeature.CandidateProfilePrimaryInformation,
		...sharedProperties,
	},
	SPProfileAuditContentNameLast: {
		defaultValue: 'Last Name',
		description: 'Text shown for Last Name.',
		displayPath: `${pathStart}Last Name`,
		displayType: 'Content',
		legacyName: 'CandidateProfile.PrimaryInformation/Candidates.ProfileAudit.Content.LastName',
		feature: HubFeature.CandidateProfilePrimaryInformation,
		...sharedProperties,
	},
	SPProfileAuditContentNameInvalid: {
		defaultValue: 'Name Invalid',
		description: 'Text shown for Name Invalid.',
		displayPath: `${pathStart}Name Invalid`,
		displayType: 'Content',
		legacyName:
			'CandidateProfile.PrimaryInformation/Candidates.ProfileAudit.Content.NameInvalid',
		feature: HubFeature.CandidateProfilePrimaryInformation,
		...sharedProperties,
	},
	SPProfileAuditContentPreferredNameFirst: {
		defaultValue: 'Preferred First Name',
		description: 'Text shown for Preferred First Name.',
		displayPath: `${pathStart}Preferred First Name`,
		displayType: 'Content',
		legacyName:
			'CandidateProfile.PrimaryInformation.PreferredName/Candidates.ProfileAudit.Content.PreferredFirstName',
		feature: HubFeature.CandidateProfilePrimaryInformationPreferredName,
		...sharedProperties,
	},
	SPProfileAuditContentPreferredNameLast: {
		defaultValue: 'Preferred Last Name',
		description: 'Text shown for Preferred Last Name.',
		displayPath: `${pathStart}Preferred Last Name`,
		displayType: 'Content',
		legacyName:
			'CandidateProfile.PrimaryInformation.PreferredName/Candidates.ProfileAudit.Content.PreferredLastName',
		feature: HubFeature.CandidateProfilePrimaryInformationPreferredName,
		...sharedProperties,
	},
	SPProfileAuditContentEmail: {
		defaultValue: 'Email',
		description: 'Text shown for Email.',
		displayPath: `${pathStart}Email`,
		displayType: 'Content',
		legacyName: 'CandidateProfile.PrimaryInformation/Candidates.ProfileAudit.Content.Email',
		feature: HubFeature.CandidateProfilePrimaryInformation,
		...sharedProperties,
	},
	SPProfileAuditContentHasPassword: {
		defaultValue: 'Has Password',
		description: 'Text shown for Has Password.',
		displayPath: `${pathStart}Has Password`,
		displayType: 'Content',
		legacyName: 'CandidateProfile/Candidates.ProfileAudit.Content.HasPassword',
		feature: HubFeature.CandidateProfile,
		...sharedProperties,
	},
	SPProfileAuditContentCompanyName: {
		defaultValue: 'Company Name',
		description: 'Text shown for Company Name.',
		displayPath: `${pathStart}Company Name`,
		displayType: 'Content',
		legacyName:
			'CandidateProfile.PrimaryInformation/Candidates.ProfileAudit.Content.CompanyName',
		feature: HubFeature.CandidateProfilePrimaryInformation,
		...sharedProperties,
	},
	SPProfileAuditContentAddr1Line1: {
		defaultValue: 'Address Line 1',
		description: 'Text shown for Address Line 1.',
		displayPath: `${pathStart}Address Line 1`,
		displayType: 'Content',
		legacyName:
			'CandidateProfile.Addresses.Primary/Candidates.ProfileAudit.Content.AddressLine1',
		feature: HubFeature.CandidateProfileAddressesPrimary,
		...sharedProperties,
	},
	SPProfileAuditContentAddr1Line2: {
		defaultValue: 'Address Line 2',
		description: 'Text shown for Address Line 2.',
		displayPath: `${pathStart}Address Line 2`,
		displayType: 'Content',
		legacyName:
			'CandidateProfile.Addresses.Primary/Candidates.ProfileAudit.Content.AddressLine2',
		feature: HubFeature.CandidateProfileAddressesPrimary,
		...sharedProperties,
	},
	SPProfileAuditContentAddr1Line3: {
		defaultValue: 'Address Line 3',
		description: 'Text shown for Address Line 3.',
		displayPath: `${pathStart}Address Line 3`,
		displayType: 'Content',
		legacyName:
			'CandidateProfile.Addresses.Primary/Candidates.ProfileAudit.Content.AddressLine3',
		feature: HubFeature.CandidateProfileAddressesPrimary,
		...sharedProperties,
	},
	SPProfileAuditContentAddr1City: {
		defaultValue: 'Address City',
		description: 'Text shown for Address City.',
		displayPath: `${pathStart}Address City`,
		displayType: 'Content',
		legacyName:
			'CandidateProfile.Addresses.Primary/Candidates.ProfileAudit.Content.AddressCity',
		feature: HubFeature.CandidateProfileAddressesPrimary,
		...sharedProperties,
	},
	SPProfileAuditContentAddr1State: {
		defaultValue: 'Address State',
		description: 'Text shown for Address State.',
		displayPath: `${pathStart}Address State`,
		displayType: 'Content',
		legacyName:
			'CandidateProfile.Addresses.Primary/Candidates.ProfileAudit.Content.AddressState',
		feature: HubFeature.CandidateProfileAddressesPrimary,
		...sharedProperties,
	},
	SPProfileAuditContentAddr1PostalCode: {
		defaultValue: 'Address Postal Code',
		description: 'Text shown for Address Postal Code.',
		displayPath: `${pathStart}Address Postal Code`,
		displayType: 'Content',
		legacyName:
			'CandidateProfile.Addresses.Primary/Candidates.ProfileAudit.Content.AddressPostalCode',
		feature: HubFeature.CandidateProfileAddressesPrimary,
		...sharedProperties,
	},
	SPProfileAuditContentAddr1CountryCode: {
		defaultValue: 'Address Country Code',
		description: 'Text shown for Address Country Code.',
		displayPath: `${pathStart}Address Country Code`,
		displayType: 'Content',
		legacyName:
			'CandidateProfile.Addresses.Primary/Candidates.ProfileAudit.Content.AddressCountryCode',
		feature: HubFeature.CandidateProfileAddressesPrimary,
		...sharedProperties,
	},
	SPProfileAuditContentAddr1PhoneNumber: {
		defaultValue: 'Address Phone Number',
		description: 'Text shown for Address Phone Number.',
		displayPath: `${pathStart}Address Phone Number`,
		displayType: 'Content',
		legacyName:
			'CandidateProfile.Addresses.Primary/Candidates.ProfileAudit.Content.AddressPhoneNumber',
		feature: HubFeature.CandidateProfileAddressesPrimary,
		...sharedProperties,
	},
	SPProfileAuditContentAddr1FaxNumber: {
		defaultValue: 'Address Fax Number',
		description: 'Text shown for Address Fax Number.',
		displayPath: `${pathStart}Address Fax Number`,
		displayType: 'Content',
		legacyName:
			'CandidateProfile.Addresses.Primary/Candidates.ProfileAudit.Content.AddressFaxNumber',
		feature: HubFeature.CandidateProfileAddressesPrimary,
		...sharedProperties,
	},
	SPProfileAuditContentAddr1Business: {
		defaultValue: 'Address Business',
		description: 'Text shown for Address Business.',
		displayPath: `${pathStart}Address Business`,
		displayType: 'Content',
		legacyName:
			'CandidateProfile.Addresses.Primary/Candidates.ProfileAudit.Content.AddressBusiness',
		feature: HubFeature.CandidateProfileAddressesPrimary,
		...sharedProperties,
	},
	SPProfileAuditContentAddr2Line1: {
		defaultValue: 'Other Address Line 1',
		description: 'Text shown for Other Address Line 1.',
		displayPath: `${pathStart}Other Address Line 1`,
		displayType: 'Content',
		legacyName:
			'CandidateProfile.Addresses.Secondary/Candidates.ProfileAudit.Content.OtherAddressLine1',
		feature: HubFeature.CandidateProfileAddressesSecondary,
		...sharedProperties,
	},
	SPProfileAuditContentAddr2Line2: {
		defaultValue: 'Other Address Line 2',
		description: 'Text shown for Other Address Line 2.',
		displayPath: `${pathStart}Other Address Line 2`,
		displayType: 'Content',
		legacyName:
			'CandidateProfile.Addresses.Secondary/Candidates.ProfileAudit.Content.OtherAddressLine2',
		feature: HubFeature.CandidateProfileAddressesSecondary,
		...sharedProperties,
	},
	SPProfileAuditContentAddr2Line3: {
		defaultValue: 'Other Address Line 3',
		description: 'Text shown for Other Address Line 3.',
		displayPath: `${pathStart}Other Address Line 3`,
		displayType: 'Content',
		legacyName:
			'CandidateProfile.Addresses.Secondary/Candidates.ProfileAudit.Content.OtherAddressLine3',
		feature: HubFeature.CandidateProfileAddressesSecondary,
		...sharedProperties,
	},
	SPProfileAuditContentAddr2City: {
		defaultValue: 'Other Address City',
		description: 'Text shown for Other Address City.',
		displayPath: `${pathStart}Other Address City`,
		displayType: 'Content',
		legacyName:
			'CandidateProfile.Addresses.Secondary/Candidates.ProfileAudit.Content.OtherAddressCity',
		feature: HubFeature.CandidateProfileAddressesSecondary,
		...sharedProperties,
	},
	SPProfileAuditContentAddr2State: {
		defaultValue: 'Other Address State',
		description: 'Text shown for Other Address State.',
		displayPath: `${pathStart}Other Address State`,
		displayType: 'Content',
		legacyName:
			'CandidateProfile.Addresses.Secondary/Candidates.ProfileAudit.Content.OtherAddressState',
		feature: HubFeature.CandidateProfileAddressesSecondary,
		...sharedProperties,
	},
	SPProfileAuditContentAddr2PostalCode: {
		defaultValue: 'Other Address Postal Code',
		description: 'Text shown for Other Address Postal Code.',
		displayPath: `${pathStart}Other Address Postal Code`,
		displayType: 'Content',
		legacyName:
			'CandidateProfile.Addresses.Secondary/Candidates.ProfileAudit.Content.OtherAddressPostalCode',
		feature: HubFeature.CandidateProfileAddressesSecondary,
		...sharedProperties,
	},
	SPProfileAuditContentAddr2CountryCode: {
		defaultValue: 'Other Address Country Code',
		description: 'Text shown for Other Address Country Code.',
		displayPath: `${pathStart}Other Address Country Code`,
		displayType: 'Content',
		legacyName:
			'CandidateProfile.Addresses.Secondary/Candidates.ProfileAudit.Content.OtherAddressCountryCode',
		feature: HubFeature.CandidateProfileAddressesSecondary,
		...sharedProperties,
	},
	SPProfileAuditContentAddr2PhoneNumber: {
		defaultValue: 'Other Address Phone Number',
		description: 'Text shown for Other Address Phone Number.',
		displayPath: `${pathStart}Other Address Phone Number`,
		displayType: 'Content',
		legacyName:
			'CandidateProfile.Addresses.Secondary/Candidates.ProfileAudit.Content.OtherAddressPhoneNumber',
		feature: HubFeature.CandidateProfileAddressesSecondary,
		...sharedProperties,
	},
	SPProfileAuditContentAddr2FaxNumber: {
		defaultValue: 'Other Address Fax Number',
		description: 'Text shown for Other Address Fax Number.',
		displayPath: `${pathStart}Other Address Fax Number`,
		displayType: 'Content',
		legacyName:
			'CandidateProfile.Addresses.Secondary/Candidates.ProfileAudit.Content.OtherAddressFaxNumber',
		feature: HubFeature.CandidateProfileAddressesSecondary,
		...sharedProperties,
	},
	SPProfileAuditContentAddr2Business: {
		defaultValue: 'Other Address Business',
		description: 'Text shown for Other Address Business.',
		displayPath: `${pathStart}Other Address Business`,
		displayType: 'Content',
		legacyName:
			'CandidateProfile.Addresses.Secondary/Candidates.ProfileAudit.Content.OtherAddressBusiness',
		feature: HubFeature.CandidateProfileAddressesSecondary,
		...sharedProperties,
	},
	SPProfileAuditContentLocNameFirst: {
		defaultValue: 'Non-Romanized First Name',
		description: 'Text shown for Non-Romanized First Name.',
		displayPath: `${pathStart}Non-Romanized First Name`,
		displayType: 'Content',
		legacyName:
			'CandidateProfile.PrimaryInformation/Candidates.ProfileAudit.Content.NonRomanizedFirstName',
		feature: HubFeature.CandidateProfilePrimaryInformation,
		...sharedProperties,
	},
	SPProfileAuditContentLocNameLast: {
		defaultValue: 'Non-Romanized Last Name',
		description: 'Text shown for Non-Romanized Last Name.',
		displayPath: `${pathStart}Non-Romanized Last Name`,
		displayType: 'Content',
		legacyName:
			'CandidateProfile.LocalizedInformation/Candidates.ProfileAudit.Content.NonRomanizedLastName',
		feature: HubFeature.CandidateProfileLocalizedInformation,
		...sharedProperties,
	},
	SPProfileAuditContentLocCompanyName: {
		defaultValue: 'Non-Romanized Company Name',
		description: 'Text shown for Non-Romanized Company Name.',
		displayPath: `${pathStart}Non-Romanized Company Name`,
		displayType: 'Content',
		legacyName:
			'CandidateProfile.LocalizedInformation/Candidates.ProfileAudit.Content.NonRomanizedCompanyName',
		feature: HubFeature.CandidateProfileLocalizedInformation,
		...sharedProperties,
	},
	SPProfileAuditContentLocAddr1Line1: {
		defaultValue: 'Non-Romanized Address Line 1',
		description: 'Text shown for Non-Romanized Address Line 1.',
		displayPath: `${pathStart}Non-Romanized Address Line 1`,
		displayType: 'Content',
		legacyName:
			'CandidateProfile.Addresses.Localized/Candidates.ProfileAudit.Content.NonRomanizedAddressLine1',
		feature: HubFeature.CandidateProfileAddressesLocalized,
		...sharedProperties,
	},
	SPProfileAuditContentLocAddr1Line2: {
		defaultValue: 'Non-Romanized Address Line 2',
		description: 'Text shown for Non-Romanized Address Line 2.',
		displayPath: `${pathStart}Non-Romanized Address Line 2`,
		displayType: 'Content',
		legacyName:
			'CandidateProfile.Addresses.Localized/Candidates.ProfileAudit.Content.NonRomanizedAddressLine2',
		feature: HubFeature.CandidateProfileAddressesLocalized,
		...sharedProperties,
	},
	SPProfileAuditContentLocAddr1Line3: {
		defaultValue: 'Non-Romanized Address Line 3',
		description: 'Text shown for Non-Romanized Address Line 3.',
		displayPath: `${pathStart}Non-Romanized Address Line 3`,
		displayType: 'Content',
		legacyName:
			'CandidateProfile.Addresses.Localized/Candidates.ProfileAudit.Content.NonRomanizedAddressLine3',
		feature: HubFeature.CandidateProfileAddressesLocalized,
		...sharedProperties,
	},
	SPProfileAuditContentLocAddr1City: {
		defaultValue: 'Non-Romanized Address City',
		description: 'Text shown for Non-Romanized Address City.',
		displayPath: `${pathStart}Non-Romanized Address City`,
		displayType: 'Content',
		legacyName:
			'CandidateProfile.Addresses.Localized/Candidates.ProfileAudit.Content.NonRomanizedAddressCity',
		feature: HubFeature.CandidateProfileAddressesLocalized,
		...sharedProperties,
	},
	SPProfileAuditContentLocAddr1State: {
		defaultValue: 'Non-Romanized Address State',
		description: 'Text shown for Non-Romanized Address State.',
		displayPath: `${pathStart}Non-Romanized Address State`,
		displayType: 'Content',
		legacyName:
			'CandidateProfile.Addresses.Localized/Candidates.ProfileAudit.Content.NonRomanizedAddressState',
		feature: HubFeature.CandidateProfileAddressesLocalized,
		...sharedProperties,
	},
	SPProfileAuditContentLocAddr1PostalCode: {
		defaultValue: 'Non-Romanized Address Postal Code',
		description: 'Text shown for Non-Romanized Address Postal Code.',
		displayPath: `${pathStart}Non-Romanized Address Postal Code`,
		displayType: 'Content',
		legacyName:
			'CandidateProfile.Addresses.Localized/Candidates.ProfileAudit.Content.NonRomanizedAddressPostalCode',
		feature: HubFeature.CandidateProfileAddressesLocalized,
		...sharedProperties,
	},
	SPProfileAuditContentAffiliation: {
		defaultValue: 'Affiliation',
		description: 'Text shown for Affiliation.',
		displayPath: `${pathStart}Affiliation`,
		displayType: 'Content',
		legacyName:
			'CandidateProfile.PrimaryInformation.Affiliation/Candidates.ProfileAudit.Content.Affiliation',
		feature: HubFeature.CandidateProfilePrimaryInformationAffiliation,
		...sharedProperties,
	},
};

export type SPProfileAuditLocType = LocMap<SPProfileAuditLocNames>;
